import { useState, useEffect } from 'react';
import { useAuth, useBusinessUnit } from '@/App';
import { statements, agents as agentsApi, commissions as commissionsApi } from '@/lib/api';
import { formatCurrency, formatPercent, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FileText, Download, Mail, DollarSign, Receipt, TrendingUp, Calendar, Trash2, RefreshCw, FileCheck, AlertCircle, ChevronDown, ChevronRight, Home, Briefcase, FileSpreadsheet, CircleCheck, Circle } from 'lucide-react';
import InsuranceStatements from '@/components/InsuranceStatements';
import * as XLSX from 'xlsx';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function getDefaultDates() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: firstDay.toISOString().split('T')[0],
    end: today.toISOString().split('T')[0]
  };
}

export default function Statements() {
  const { user } = useAuth();
  const { businessUnit, switchBusinessUnit } = useBusinessUnit();
  const isAgent = user?.role === 'agent';
  const defaultDates = getDefaultDates();
  
  // Original statement generation state - these hooks must be called regardless of businessUnit
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [statement, setStatement] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [agentId, setAgentId] = useState(isAgent ? user?.agent_id || '' : '');
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  
  // Stored statements state
  const [storedStatements, setStoredStatements] = useState([]);
  const [loadingStored, setLoadingStored] = useState(false);
  const [paymentDates, setPaymentDates] = useState([]);
  const [selectedPaymentDate, setSelectedPaymentDate] = useState('');
  const [generatingByDate, setGeneratingByDate] = useState(false);
  const [activeTab, setActiveTab] = useState('generated');
  const [expandedDates, setExpandedDates] = useState({});
  const [expandedYears, setExpandedYears] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Commission data for search (mortgage)
  const [commissions, setCommissions] = useState([]);

  // Pay period state (for insurance)
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [payPeriodYear, setPayPeriodYear] = useState(new Date().getFullYear());
  const [dateSelectionMode, setDateSelectionMode] = useState('custom');

  // Toggle expand/collapse for a payment date
  function toggleDateExpanded(date) {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  }

  // Toggle expand/collapse for a year
  function toggleYearExpanded(year) {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  }

  useEffect(function() {
    if (businessUnit === 'mortgage') {
      if (isAgent && user?.agent_id) {
        setAgentId(user.agent_id);
        setLoading(false);
      } else {
        loadAgents();
      }
      loadStoredStatements();
      loadPaymentDates();
      loadCommissions();
    }
  }, [isAgent, user, businessUnit]);

  // Load pay periods when year changes (for insurance)
  useEffect(() => {
    if (businessUnit === 'insurance') {
      loadPayPeriods(payPeriodYear);
    }
  }, [businessUnit, payPeriodYear]);

  async function loadCommissions() {
    try {
      const response = await commissionsApi.list();
      setCommissions(response.data || []);
    } catch (err) {
      toast.error('Failed to load commissions');
    }
  }

  async function loadAgents() {
    try {
      const response = await agentsApi.list();
      setAgents(response.data);
    } catch (err) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  }

  async function loadStoredStatements() {
    setLoadingStored(true);
    try {
      const response = await statements.getStored();
      setStoredStatements(response.data);
    } catch (err) {
      toast.error('Failed to load statements');
    } finally {
      setLoadingStored(false);
    }
  }

  async function loadPaymentDates() {
    try {
      const response = await statements.getPaymentDates();
      setPaymentDates(response.data.payment_dates || []);
    } catch (err) {
      toast.error('Failed to load payment dates');
    }
  }

  async function loadPayPeriods(year) {
    try {
      const response = await statements.getPayPeriods(year);
      setPayPeriods(response.data.periods || []);
    } catch (err) {
      toast.error('Failed to load pay periods');
    }
  }

  function handlePayPeriodSelect(periodNumber) {
    const period = payPeriods.find(p => p.period_number === periodNumber);
    if (period) {
      setSelectedPayPeriod(period);
      setStartDate(period.start_date);
      setEndDate(period.end_date);
    }
  }
  
  // If insurance business unit, show insurance statements
  if (businessUnit === 'insurance') {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="statements-page">
        {/* Business Unit Tabs */}
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant={businessUnit === 'mortgage' ? 'default' : 'outline'}
            size="sm"
            onClick={() => switchBusinessUnit('mortgage')}
            className="flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Mortgage
          </Button>
          <Button
            variant={businessUnit === 'insurance' ? 'default' : 'outline'}
            size="sm"
            onClick={() => switchBusinessUnit('insurance')}
            className="flex items-center gap-2"
          >
            <Briefcase className="w-4 h-4" />
            Insurance & Investment
          </Button>
        </div>
        
        <InsuranceStatements />
      </div>
    );
  }

  async function handleGenerate() {
    const selectedAgentId = isAgent ? user?.agent_id : agentId;
    if (!selectedAgentId) {
      toast.error('Please select an agent');
      return;
    }
    setGenerating(true);
    try {
      const response = await statements.generate({
        agent_id: selectedAgentId,
        start_date: startDate,
        end_date: endDate
      });
      setStatement(response.data);
      const agent = agents.find(function(a) { return a.id === agentId; });
      setEmailAddress(agent ? agent.email : '');
      toast.success('Statement generated');
    } catch (err) {
      toast.error('Failed to generate statement');
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownloadPdf() {
    const selectedAgentId = isAgent ? user?.agent_id : agentId;
    try {
      const response = await statements.downloadPdf({
        agent_id: selectedAgentId,
        start_date: startDate,
        end_date: endDate
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const agentName = statement.agent.name.replace(/\s/g, '_');
      link.download = 'statement_' + agentName + '_' + startDate + '_' + endDate + '.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  }

  async function handleSendEmail() {
    const selectedAgentId = isAgent ? user?.agent_id : agentId;
    if (!emailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      await statements.email({
        agent_id: selectedAgentId,
        start_date: startDate,
        end_date: endDate,
        email: emailAddress
      });
      toast.success('Statement sent to ' + emailAddress);
      setShowEmailModal(false);
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.detail;
      toast.error(msg || 'Failed to send email');
    }
  }

  async function handleGenerateByPaymentDate() {
    if (!selectedPaymentDate) {
      toast.error('Please select a payment date');
      return;
    }
    setGeneratingByDate(true);
    try {
      const response = await statements.generateByPaymentDate({
        payment_date: selectedPaymentDate
      });
      toast.success(response.data.message);
      loadStoredStatements();
      setActiveTab('generated');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to generate statements';
      toast.error(msg);
    } finally {
      setGeneratingByDate(false);
    }
  }

  async function handleDownloadStoredPdf(statementId, agentName, paymentDate) {
    try {
      const response = await statements.downloadStoredPdf(statementId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement_${agentName.replace(/\s/g, '_')}_${paymentDate}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  }

  async function handleDeleteStored(statementId) {
    if (!window.confirm('Are you sure you want to delete this statement?')) return;
    try {
      await statements.deleteStored(statementId);
      toast.success('Statement deleted');
      loadStoredStatements();
    } catch (err) {
      toast.error('Failed to delete statement');
    }
  }

  async function handleEmailStored(statementId, agentEmail) {
    try {
      await statements.emailStored(statementId);
      toast.success(`Statement will be emailed to ${agentEmail}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send email';
      toast.error(msg);
    }
  }

  function handleDownloadExcel(year, dateGroups) {
    const rows = [];
    Object.entries(dateGroups).sort((a, b) => a[0].localeCompare(b[0])).forEach(([date, stmts]) => {
      stmts.forEach(stmt => {
        rows.push({
          'Payment Date': date,
          'Agent Name': stmt.agent_name,
          'Agent Email': stmt.agent_email || '',
          'Total Commissions': stmt.total_commissions || 0,
          'Total Expenses': stmt.total_expenses || 0,
          'Net Commission': stmt.net_commission || 0,
          'Commission Count': stmt.commission_count || 0,
          'Expense Count': stmt.expense_count || 0,
        });
      });
    });
    if (rows.length === 0) { toast.error('No data to export'); return; }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 28 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Statements ${year}`);
    XLSX.writeFile(wb, `Mortgage_Statements_${year}.xlsx`);
    toast.success(`Downloaded ${rows.length} statements for ${year}`);
  }

  // Toggle paid status on a mortgage commission
  async function handleMarkCommPaid(commissionId, currentPaid) {
    const newPaid = !currentPaid;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/commissions/${commissionId}/mark-paid`,
        { paid: newPaid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      if (statement) {
        setStatement(prev => ({
          ...prev,
          commissions: prev.commissions.map(c =>
            c.id === commissionId ? { ...c, paid: newPaid } : c
          )
        }));
      }
      toast.success(newPaid ? 'Marked as paid' : 'Marked as unpaid');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update paid status');
    }
  }

  // Bulk mark all commissions for a payment date as paid/unpaid
  async function handleBulkMarkPaid(paymentDate, markAsPaid) {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API}/api/commissions/bulk/mark-paid`,
        { payment_date: paymentDate, paid: markAsPaid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      // Reload statements to reflect new status
      loadStoredStatements();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update paid status');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const totalCommission = statement ? statement.summary.total_commission : 0;
  const totalExpenses = statement ? statement.summary.total_expenses : 0;
  const netCommission = statement ? (statement.summary.net_commission !== undefined ? statement.summary.net_commission : totalCommission - totalExpenses) : 0;

  // Filter stored statements by search term - search all commission data
  const filteredStatements = storedStatements.filter(stmt => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    
    // Search statement fields
    if (
      (stmt.agent_name && stmt.agent_name.toLowerCase().includes(term)) ||
      (stmt.agent_email && stmt.agent_email.toLowerCase().includes(term)) ||
      (stmt.payment_date && stmt.payment_date.includes(term))
    ) {
      return true;
    }
    
    // Search in commission data for this agent/payment date
    const matchingCommissions = commissions.filter(c => {
      // Match by agent and payment date if available
      const matchesAgent = c.agent_id === stmt.agent_id || 
        (c.agent_name && c.agent_name.toLowerCase() === stmt.agent_name?.toLowerCase());
      const matchesDate = c.payment_date === stmt.payment_date || 
        c.statement_date === stmt.payment_date;
      
      if (!matchesAgent) return false;
      
      // Search all commission fields
      return (
        (c.description && c.description.toLowerCase().includes(term)) ||
        (c.lender && c.lender.toLowerCase().includes(term)) ||
        (c.product && c.product.toLowerCase().includes(term)) ||
        (c.velocity_ref && c.velocity_ref.toLowerCase().includes(term)) ||
        (c.memo && c.memo.toLowerCase().includes(term)) ||
        (c.dlc_statement_no && c.dlc_statement_no.toLowerCase().includes(term)) ||
        (c.co_agent_name && c.co_agent_name.toLowerCase().includes(term)) ||
        // Search numeric fields
        (c.mortgage_loan && c.mortgage_loan.toString().includes(term)) ||
        (c.gross_commission && c.gross_commission.toString().includes(term)) ||
        (c.agent_commission && c.agent_commission.toString().includes(term))
      );
    });
    
    return matchingCommissions.length > 0;
  });

  // Group filtered statements by year, then by payment date
  const statementsByYear = filteredStatements.reduce((acc, stmt) => {
    const date = stmt.payment_date;
    const year = date ? date.substring(0, 4) : 'Unknown';
    if (!acc[year]) acc[year] = {};
    if (!acc[year][date]) acc[year][date] = [];
    acc[year][date].push(stmt);
    return acc;
  }, {});

  // Calculate year totals
  const getYearTotals = (yearData) => {
    let totalCommissions = 0;
    let totalExpenses = 0;
    let totalNet = 0;
    let statementCount = 0;
    
    Object.values(yearData).forEach(dateStmts => {
      dateStmts.forEach(stmt => {
        totalCommissions += stmt.total_commissions || 0;
        totalExpenses += stmt.total_expenses || 0;
        totalNet += stmt.net_commission || 0;
        statementCount++;
      });
    });
    
    return { totalCommissions, totalExpenses, totalNet, statementCount };
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="statements-page">
      {/* Business Unit Tabs */}
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant={businessUnit === 'mortgage' ? 'default' : 'outline'}
          size="sm"
          onClick={() => switchBusinessUnit('mortgage')}
          className="flex items-center gap-2"
        >
          <Home className="w-4 h-4" />
          Mortgage
        </Button>
        <Button
          variant={businessUnit === 'insurance' ? 'default' : 'outline'}
          size="sm"
          onClick={() => switchBusinessUnit('insurance')}
          className="flex items-center gap-2"
        >
          <Briefcase className="w-4 h-4" />
          Insurance & Investment
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {isAgent ? 'My Statements' : 'Statements'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isAgent ? 'View and download your commission statements' : 'Generate, manage, and send commission statements to agents'}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="generated" className="flex items-center gap-2" data-testid="tab-generated">
            <FileCheck className="w-4 h-4" />
            Generated Statements
          </TabsTrigger>
          <TabsTrigger value="generate" className="flex items-center gap-2" data-testid="tab-generate">
            <FileText className="w-4 h-4" />
            {isAgent ? 'Generate My Statement' : 'Generate New Statement'}
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Generated Statements (Stored) */}
        <TabsContent value="generated" className="space-y-4">
          {/* Admin: Generate by Payment Date */}
          {!isAgent && (
            <Card className="border-slate-100 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Calendar className="w-5 h-5" /> Generate Statements by Payment Date
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Payment Date</Label>
                    <Select value={selectedPaymentDate} onValueChange={setSelectedPaymentDate}>
                      <SelectTrigger data-testid="payment-date-select">
                        <SelectValue placeholder="Select a payment date" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentDates.map(function(date) {
                          return <SelectItem key={date} value={date}>{formatDate(date)}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Generates PDF statements for all agents with commissions on this payment date
                    </p>
                  </div>
                  <Button
                    onClick={handleGenerateByPaymentDate}
                    disabled={generatingByDate || !selectedPaymentDate}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="generate-by-date-btn"
                  >
                    {generatingByDate ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileCheck className="w-4 h-4 mr-2" />
                        Generate Statements
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stored Statements List */}
          <Card className="border-slate-100 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileCheck className="w-5 h-5" /> 
                {isAgent ? 'My Generated Statements' : 'All Generated Statements'}
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search all data..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 pl-8"
                    data-testid="statement-search"
                  />
                  <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <Button variant="outline" size="sm" onClick={loadStoredStatements} disabled={loadingStored}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingStored ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingStored ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                </div>
              ) : filteredStatements.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  {searchTerm ? (
                    <>
                      <p className="font-medium">No statements match your search</p>
                      <p className="text-sm mt-1">Try a different search term or clear the search.</p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium">No statements generated yet</p>
                      <p className="text-sm mt-1">
                        {isAgent 
                          ? 'Your administrator will generate statements for you.' 
                          : 'Select a payment date above to generate statements for all agents.'}
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {searchTerm && (
                    <p className="text-sm text-slate-500 mb-2">
                      Showing {filteredStatements.length} of {storedStatements.length} statements
                    </p>
                  )}
                  {Object.entries(statementsByYear).sort((a, b) => b[0].localeCompare(a[0])).map(([year, dateGroups]) => {
                    const isYearExpanded = expandedYears[year] === true; // Default collapsed
                    const yearTotals = getYearTotals(dateGroups);
                    const paymentDatesCount = Object.keys(dateGroups).length;
                    
                    return (
                      <Card key={year} className="border-slate-200 shadow-sm overflow-hidden">
                        {/* Year Header */}
                        <div 
                          className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-white cursor-pointer hover:bg-emerald-100 transition-colors flex items-center justify-between"
                          onClick={() => toggleYearExpanded(year)}
                          data-testid={`year-header-${year}`}
                        >
                          <div className="flex items-center gap-3">
                            {isYearExpanded ? (
                              <ChevronDown className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-emerald-600" />
                            )}
                            <Calendar className="w-5 h-5 text-emerald-600" />
                            <span className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Manrope' }}>
                              {year}
                            </span>
                            <span className="text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {yearTotals.statementCount} statement{yearTotals.statementCount !== 1 ? 's' : ''} • {paymentDatesCount} payment date{paymentDatesCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8"
                              onClick={(e) => { e.stopPropagation(); handleDownloadExcel(year, dateGroups); }}
                              title={`Download ${year} statements as Excel`}
                              data-testid={`download-year-${year}`}
                            >
                              <FileSpreadsheet className="w-4 h-4 mr-1 text-emerald-600" />
                              Excel
                            </Button>
                            <div className="text-right">
                              <span className="text-slate-500 text-xs block">Total Commissions</span>
                              <span className="font-mono font-bold text-emerald-600">{formatCurrency(yearTotals.totalCommissions)}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-slate-500 text-xs block">Total Expenses</span>
                              <span className="font-mono font-bold text-amber-600">({formatCurrency(yearTotals.totalExpenses)})</span>
                            </div>
                            <div className="text-right">
                              <span className="text-slate-500 text-xs block">Total Net</span>
                              <span className="font-mono font-bold text-slate-900">{formatCurrency(yearTotals.totalNet)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Year Content - Payment Dates */}
                        {isYearExpanded && (
                          <div className="border-t border-slate-200 p-4 space-y-3 bg-slate-50/50">
                            {Object.entries(dateGroups).sort((a, b) => b[0].localeCompare(a[0])).map(([date, stmts]) => {
                              const isExpanded = expandedDates[date] || false;
                              const totalCommissions = stmts.reduce((sum, s) => sum + s.total_commissions, 0);
                              const totalNet = stmts.reduce((sum, s) => sum + s.net_commission, 0);
                              const totalCommCount = stmts.reduce((sum, s) => sum + (s.commission_count || 0), 0);
                              const totalPaidCount = stmts.reduce((sum, s) => sum + (s.paid_count || 0), 0);
                              const allPaid = totalCommCount > 0 && totalPaidCount === totalCommCount;
                              
                              return (
                                <Collapsible key={date} open={isExpanded} onOpenChange={() => toggleDateExpanded(date)}>
                                  <CollapsibleTrigger asChild>
                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
                                      <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                          <ChevronDown className="w-4 h-4 text-slate-500" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-slate-500" />
                                        )}
                                        <FileText className="w-4 h-4 text-blue-600" />
                                        <h3 className="font-semibold text-slate-700">Payment Date: {formatDate(date)}</h3>
                                        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                          {stmts.length} statement{stmts.length > 1 ? 's' : ''}
                                        </span>
                                        {allPaid && (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                                            <CircleCheck className="w-3 h-3" />ALL PAID
                                          </span>
                                        )}
                                        {!allPaid && totalPaidCount > 0 && (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                                            {totalPaidCount}/{totalCommCount} Paid
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-4 text-sm">
                                        {!isAgent && totalCommCount > 0 && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className={allPaid
                                              ? "text-amber-600 border-amber-200 hover:bg-amber-50 text-xs h-7"
                                              : "text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs h-7"
                                            }
                                            onClick={(e) => { e.stopPropagation(); handleBulkMarkPaid(date, !allPaid); }}
                                            data-testid={`bulk-mark-paid-${date}`}
                                          >
                                            {allPaid ? (
                                              <><Circle className="w-3 h-3 mr-1" />Mark All Unpaid</>
                                            ) : (
                                              <><CircleCheck className="w-3 h-3 mr-1" />Mark All Paid</>
                                            )}
                                          </Button>
                                        )}
                                        <div className="text-right">
                                          <span className="text-slate-500 text-xs">Commissions</span>
                                          <p className="font-mono font-semibold text-emerald-600">{formatCurrency(totalCommissions)}</p>
                                        </div>
                                        <div className="text-right">
                                          <span className="text-slate-500 text-xs">Net</span>
                                          <p className="font-mono font-semibold text-slate-900">{formatCurrency(totalNet)}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <div className="border border-t-0 border-slate-200 rounded-b-lg overflow-hidden mt-0 bg-white">
                                      <Table>
                                        <TableHeader>
                                          <TableRow className="bg-slate-50">
                                            <TableHead className="font-semibold">Agent</TableHead>
                                            <TableHead className="text-right font-semibold">Commissions</TableHead>
                                            <TableHead className="text-right font-semibold">Expenses</TableHead>
                                            <TableHead className="text-right font-semibold">Net</TableHead>
                                            <TableHead className="text-center font-semibold">Transactions</TableHead>
                                            <TableHead className="text-center font-semibold">Status</TableHead>
                                            <TableHead className="text-right font-semibold">Actions</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {stmts.map(function(stmt) {
                                            return (
                                              <TableRow key={stmt.id} className="hover:bg-slate-50">
                                                <TableCell>
                                                  <div>
                                                    <p className="font-medium text-slate-900">{stmt.agent_name}</p>
                                                    <p className="text-xs text-slate-500">{stmt.agent_email}</p>
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-right font-mono text-emerald-600">
                                                  {formatCurrency(stmt.total_commissions)}
                                                </TableCell>
                                                <TableCell className="text-right font-mono text-amber-600">
                                                  ({formatCurrency(stmt.total_expenses)})
                                                </TableCell>
                                                <TableCell className="text-right font-mono font-bold text-slate-900">
                                                  {formatCurrency(stmt.net_commission)}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                  <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded">
                                                    {stmt.commission_count} comm / {stmt.expense_count} exp
                                                  </span>
                                                </TableCell>
                                                <TableCell className="text-center">
                                                  {stmt.paid_status === 'all' ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                                                      <CircleCheck className="w-3 h-3" />PAID
                                                    </span>
                                                  ) : stmt.paid_status === 'partial' ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                                                      {stmt.paid_count}/{stmt.commission_count} Paid
                                                    </span>
                                                  ) : (
                                                    <span className="text-xs text-slate-400">-</span>
                                                  )}
                                                </TableCell>
                                                <TableCell>
                                                  <div className="flex items-center justify-end gap-1">
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      onClick={() => handleDownloadStoredPdf(stmt.id, stmt.agent_name, stmt.payment_date)}
                                                      title="Download PDF"
                                                      data-testid={`download-stmt-${stmt.id}`}
                                                    >
                                                      <Download className="w-4 h-4 text-slate-600" />
                                                    </Button>
                                                    {!isAgent && (
                                                      <>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          onClick={() => handleEmailStored(stmt.id, stmt.agent_email)}
                                                          title="Email to Agent"
                                                          data-testid={`email-stmt-${stmt.id}`}
                                                        >
                                                          <Mail className="w-4 h-4 text-emerald-600" />
                                                        </Button>
                                                        <Button
                                                          variant="ghost"
                                                          size="sm"
                                                          onClick={() => handleDeleteStored(stmt.id)}
                                                          title="Delete Statement"
                                                          data-testid={`delete-stmt-${stmt.id}`}
                                                        >
                                                          <Trash2 className="w-4 h-4 text-red-500" />
                                                        </Button>
                                                      </>
                                                    )}
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                            );
                                          })}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              );
                            })}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: Generate New Statement (Original Functionality) */}
        <TabsContent value="generate" className="space-y-6">
          <Card className="border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileText className="w-5 h-5" /> {isAgent ? 'Generate My Statement' : 'Generate Statement by Date Range'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Pay Period Selector (Insurance only) */}
              {businessUnit === 'insurance' && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">Select Pay Period</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-blue-700">Year</Label>
                      <Select value={payPeriodYear.toString()} onValueChange={(v) => setPayPeriodYear(parseInt(v))}>
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2025, 2026, 2027, 2028].map(y => (
                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-blue-700">Pay Period (Bi-weekly)</Label>
                      <Select 
                        value={selectedPayPeriod?.period_number?.toString() || ''} 
                        onValueChange={(v) => handlePayPeriodSelect(parseInt(v))}
                      >
                        <SelectTrigger className="bg-white" data-testid="pay-period-select">
                          <SelectValue placeholder="Select pay period" />
                        </SelectTrigger>
                        <SelectContent>
                          {payPeriods.map(p => (
                            <SelectItem key={p.period_number} value={p.period_number.toString()}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {selectedPayPeriod && (
                    <div className="mt-3 text-sm text-blue-700 bg-blue-100 p-2 rounded">
                      <span className="font-semibold">Payment Date:</span> {formatDate(selectedPayPeriod.payment_date)}
                    </div>
                  )}
                </div>
              )}

              {/* Custom Date Range (always available) */}
              <div className={`grid grid-cols-1 gap-4 ${isAgent ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                {!isAgent && (
                  <div className="space-y-2">
                    <Label>Agent</Label>
                    <Select value={agentId} onValueChange={setAgentId}>
                      <SelectTrigger data-testid="statement-agent-select">
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        {agents.map(function(a) {
                          return <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Start Date {businessUnit === 'insurance' && <span className="text-slate-400">(or custom)</span>}</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={function(e) { setStartDate(e.target.value); setSelectedPayPeriod(null); }}
                    data-testid="start-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={function(e) { setEndDate(e.target.value); setSelectedPayPeriod(null); }}
                    data-testid="end-date-input"
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={handleGenerate}
                    disabled={generating || (!isAgent && !agentId)}
                    className="bg-slate-900 hover:bg-slate-800 w-full"
                    data-testid="generate-statement-btn"
                  >
                    {generating ? 'Generating...' : 'Generate'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {statement && (
            <Card className="border-slate-100 shadow-sm" data-testid="statement-preview">
              <CardHeader className="border-b border-slate-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                      Commission Statement
                    </CardTitle>
                    <p className="text-slate-500 mt-1">
                      {statement.agent.name} - {formatDate(statement.period.start)} to {formatDate(statement.period.end)}
                      {statement.pay_period?.is_valid && (
                        <span className="ml-2 text-blue-600 font-medium">
                          ({statement.pay_period.label})
                        </span>
                      )}
                    </p>
                    {statement.pay_period?.is_valid && (
                      <p className="text-sm text-slate-400 mt-1">
                        Payment Date: {formatDate(statement.pay_period.payment_date)}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                    <Button
                      onClick={function() { setShowEmailModal(true); }}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="email-statement-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" /> Email Statement
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* Company Header */}
                <div className="flex flex-col items-center mb-6 pb-4 border-b border-slate-200">
                  <img src="/logo.png" alt="Dominion Lending Centres - Utmost Financial" className="h-28 mb-2" />
                  <div className="text-center text-sm text-slate-600">
                    <p className="font-semibold text-slate-700">A-970 Sargent Ave., Winnipeg, MB R3E 0E3</p>
                    <p>Phone: 1-877-239-9060 | Email: hello@utmostfinancial.ca</p>
                  </div>
                </div>

                {/* Agent Info */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Agent</p>
                      <p className="font-semibold text-slate-900">{statement.agent.name}</p>
                      {statement.agent.address && (
                        <p className="text-sm text-slate-600 mt-1">{statement.agent.address}</p>
                      )}
                      {statement.agent.email && (
                        <p className="text-sm text-slate-600 mt-1">{statement.agent.email}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Statement Period</p>
                      <p className="font-semibold text-slate-900">
                        {formatDate(statement.period.start)} - {formatDate(statement.period.end)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-emerald-700 uppercase tracking-wider">Total Commissions</p>
                        <p className="text-xl font-bold font-mono text-emerald-700">
                          {formatCurrency(totalCommission)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-amber-700 uppercase tracking-wider">Less: Total Expenses</p>
                        <p className="text-xl font-bold font-mono text-amber-700">
                          ({formatCurrency(totalExpenses)})
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-900 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-slate-900" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-300 uppercase tracking-wider">Net Commission</p>
                        <p className="text-xl font-bold font-mono text-white">
                          {formatCurrency(netCommission)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Commissions Table with all headers */}
                {statement.commissions.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Commissions</h3>
                    <div className="border border-slate-200 rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-900">
                            <TableHead className="text-white text-center text-xs">DLC Statement #</TableHead>
                            <TableHead className="text-white text-center text-xs">Lender</TableHead>
                            <TableHead className="text-white text-center text-xs">Velocity Ref.</TableHead>
                            <TableHead className="text-white text-center text-xs">Description</TableHead>
                            <TableHead className="text-white text-center text-xs">Mortgage Loan</TableHead>
                            <TableHead className="text-white text-center text-xs">FF %</TableHead>
                            <TableHead className="text-white text-center text-xs">FF Comm</TableHead>
                            <TableHead className="text-white text-center text-xs">VB %</TableHead>
                            <TableHead className="text-white text-center text-xs">VB Comm</TableHead>
                            <TableHead className="text-white text-center text-xs">Extra Bonus %</TableHead>
                            <TableHead className="text-white text-center text-xs">Extra Bonus Comm</TableHead>
                            <TableHead className="text-white text-center text-xs">Gross Comm</TableHead>
                            <TableHead className="text-white text-center text-xs">5% Royalty + GST</TableHead>
                            <TableHead className="bg-emerald-600 text-white text-center text-xs font-bold">Net Comm</TableHead>
                            <TableHead className="text-white text-center text-xs">Agent&apos;s Split</TableHead>
                            <TableHead className="bg-blue-600 text-white text-center text-xs font-bold">Agent&apos;s Comm</TableHead>
                            <TableHead className="text-white text-center text-xs">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {statement.commissions.map(function(c, i) {
                            const agentComm = c.agent_id === agentId ? c.agent_commission : c.co_agent_commission;
                            const agentSplit = c.agent_id === agentId ? c.agent_split_percentage : c.co_agent_split_percentage;
                            return (
                              <TableRow key={i} className="text-xs">
                                <TableCell className="font-mono text-center">{c.dlc_statement_no}</TableCell>
                                <TableCell className="text-center">{c.lender}</TableCell>
                                <TableCell className="text-center">{c.velocity_ref || '-'}</TableCell>
                                <TableCell title={c.description}>{c.description}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(c.mortgage_loan)}</TableCell>
                                <TableCell className="text-right font-mono">{formatPercent(c.ff_percentage)}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(c.ff_commission)}</TableCell>
                                <TableCell className="text-right font-mono">{formatPercent(c.vb_percentage)}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(c.vb_commission)}</TableCell>
                                <TableCell className="text-right font-mono">{formatPercent(c.extra_bonus_percentage)}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(c.extra_bonus_commission)}</TableCell>
                                <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(c.gross_commission)}</TableCell>
                                <TableCell className="text-right font-mono text-amber-600">{formatCurrency(c.royalty_plus_gst)}</TableCell>
                                <TableCell className="text-right font-mono font-bold bg-emerald-50 text-emerald-700">{formatCurrency(c.net_commission)}</TableCell>
                                <TableCell className="text-right font-mono">{formatPercent(agentSplit)}</TableCell>
                                <TableCell className="text-right font-mono font-bold bg-blue-50 text-blue-700">{formatCurrency(agentComm)}</TableCell>
                                <TableCell className="text-center">
                                  {!isAgent ? (
                                    <button
                                      onClick={() => handleMarkCommPaid(c.id, c.paid)}
                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                        c.paid
                                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                      }`}
                                      title={c.paid ? 'Click to mark as unpaid' : 'Click to mark as paid'}
                                      data-testid={`mark-paid-${c.id}`}
                                    >
                                      {c.paid ? <CircleCheck className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                                      {c.paid ? 'Paid' : 'Unpaid'}
                                    </button>
                                  ) : (
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${
                                      c.paid ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                      {c.paid ? <><CircleCheck className="w-3.5 h-3.5" />Paid</> : <><Circle className="w-3.5 h-3.5" />Unpaid</>}
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                        <TableFooter>
                          <TableRow className="bg-slate-100 font-bold text-xs">
                            <TableCell colSpan={15} className="text-right">Subtotal:</TableCell>
                            <TableCell className="text-right font-mono text-emerald-700">
                              {formatCurrency(totalCommission)}
                            </TableCell>
                            <TableCell className="text-center text-xs">
                              {(() => {
                                const paidCount = statement.commissions.filter(c => c.paid).length;
                                const total = statement.commissions.length;
                                if (paidCount === total) return <span className="text-emerald-600 font-semibold">All Paid</span>;
                                if (paidCount > 0) return <span className="text-amber-600">{paidCount}/{total}</span>;
                                return null;
                              })()}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Expenses Table */}
                {statement.expenses.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Expenses</h3>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-amber-500">
                            <TableHead className="text-white text-center">Date</TableHead>
                            <TableHead className="text-white text-center">Description</TableHead>
                            <TableHead className="text-white text-center">Amount</TableHead>
                            <TableHead className="text-white text-center">Tax</TableHead>
                            <TableHead className="text-white text-center">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {statement.expenses.map(function(e, i) {
                            return (
                              <TableRow key={i}>
                                <TableCell className="font-mono text-sm text-center">{formatDate(e.expense_date)}</TableCell>
                                <TableCell className="text-center">{e.description}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(e.amount)}</TableCell>
                                <TableCell className="text-right font-mono">{formatCurrency(e.tax)}</TableCell>
                                <TableCell className="text-right font-mono text-amber-600 font-medium">
                                  {formatCurrency(e.total_amount)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                        <TableFooter>
                          <TableRow className="bg-amber-50 font-bold">
                            <TableCell colSpan={4} className="text-right">Subtotal:</TableCell>
                            <TableCell className="text-right font-mono text-amber-700">
                              {formatCurrency(totalExpenses)}
                            </TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </div>
                  </div>
                )}

                {statement.commissions.length === 0 && (
                  <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg mb-4">
                    No commissions found for this period
                  </div>
                )}

                {statement.expenses.length === 0 && (
                  <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg mb-4">
                    No expenses found for this period
                  </div>
                )}

                {/* Final Net Commission Box */}
                <div className="mt-6 p-4 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-300 uppercase tracking-wider">Net Commission Payable</p>
                      <p className="text-xs text-slate-400 mt-1">
                        (Total Commissions - Total Expenses)
                      </p>
                    </div>
                    <p className="text-3xl font-bold font-mono">
                      {formatCurrency(netCommission)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent data-testid="email-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Email Statement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              Send this commission statement to {statement && statement.agent ? statement.agent.name : 'the agent'}.
            </p>
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={emailAddress}
                onChange={function(e) { setEmailAddress(e.target.value); }}
                placeholder="agent@example.com"
                data-testid="email-address-input"
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              <strong>Note:</strong> Email sending requires SMTP credentials to be configured in environment settings.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowEmailModal(false); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" /> Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
